/**
 * Fase 0 (legalização) — classificador de ROTA da alteração societária.
 *
 * Deriva, a partir dos `tipos_alteracao` marcados, se a alteração segue a
 * ROTA A (COM consulta de viabilidade prévia na junta/prefeitura → protocolo
 * de viabilidade) ou a ROTA B (SEM viabilidade → transmite direto no REDESIM).
 * É a "trava" mais comum do processo de legalização (guia do contador): errar a
 * rota faz o operador refazer o caminho na junta.
 *
 * NÃO confundir com `derivarPatchesV3.ts` — aquele decide o MOTOR do documento
 * (v2/v3, patches). Este decide o CAMINHO na junta. Eixos ortogonais, mesmo input.
 *
 * Regra (1ª ordem, do guia; confirmar com operador):
 * - ROTA A = muda nome / endereço / objeto (atividade) / natureza jurídica →
 *   precisa viabilidade.
 * - ROTA B = muda capital / quadro de sócios / administração → sem viabilidade.
 * - MISTO (A + B juntos) → ROTA A: se QUALQUER mudança exige viabilidade, é uma
 *   viabilidade só, e o resto vai junto. (Mesma forma do `outras`→v2 em
 *   derivarPatchesV3: um item marcado decide o roteamento do lote todo.)
 * - `outras` (oculto no wizard; só em contratos legados) → indeterminado, trata
 *   como provável A e sinaliza `revisar`.
 */
import type { TipoAlteracao } from '../../../../hooks/useSocietario';

// Backbone federal (REDESIM/DREI) — a viabilidade é exigida por SEIS eventos, iguais
// nas três juntas (validado 2026-07-09 contra fonte primária, V-ROTA-01):
//   Atividade econômica · Endereço · Nome empresarial · Natureza jurídica ·
//   Tipo de unidade · Forma de atuação.
// O enum `TipoAlteracao` cobre 4 deles (endereco, razao_social, objeto_social e,
// desde 2026-08-27, natureza_juridica):
//   - `natureza_juridica` ENTROU a pedido do operador. Antes ficava de fora com o
//     argumento de que troca de natureza e TRANSFORMACAO, de fluxo proprio - mas
//     isso confunde GERAR O DOCUMENTO com PERCORRER A JUNTA. O card da legalizacao
//     rastreia o segundo: a troca de natureza exige viabilidade sempre, inclusive
//     quando nenhum gerador nosso produz o instrumento (so a transformacao EI/MEI
//     -> LTDA tem gerador). Sem ele marcado, o card caia em Rota B e pulava a
//     etapa de viabilidade. Segue `oculto` no wizard - la quem gera e a operacao
//     `transformacao`.
//   - `tipo_de_unidade` e `forma_de_atuacao` NÃO são representáveis no wizard hoje
//     (raros em alteração de matriz). Quando ocorrerem, o operador marca `outras` —
//     que já cai em `revisar` (guard abaixo). Suporte pleno = adiado (V-ROTA-01).
const ROTA_A_TIPOS: ReadonlySet<TipoAlteracao> = new Set<TipoAlteracao>([
  'endereco',
  'razao_social',
  // Confirmado pelo operador em 2026-08-13, com a ressalva de que a exigencia
  // e' CONDICIONAL: quando o nome fantasia consta de clausula do contrato
  // social, mudar exige alteracao contratual, e "em alguns estados ou sistemas
  // locais da Redesim, pode ser exigido o processo de viabilidade ou ato
  // proprio de atualizacao".
  //
  // Ou seja: nem sempre exige, mas quando exige o custo e' o operador refazer
  // o caminho na junta. Por isso A — o erro pra B e' caro, o erro pra A e' uma
  // viabilidade a mais.
  'nome_fantasia',
  'objeto_social',
  'natureza_juridica',
  // Filial nova passa pela viabilidade no município dela.
  'abertura_filial',
  // Idem a filial alterada (endereço, atividades, capital destacado ou nome fantasia). A rota
  // sai do tipo, não do que mudou: mapear por dado fica para o roadmap da legalização (09/10).
  'alteracao_filial',
]);
const ROTA_B_TIPOS: ReadonlySet<TipoAlteracao> = new Set<TipoAlteracao>([
  'capital',
  'quadro_societario',
  'dados_socio',
  'administracao',
  // Encerrar filial é baixa: não há local nem nome novo para a viabilidade analisar (08/10,
  // a confirmar com o jurídico).
  'extincao_filial',
  // Outras cláusulas (15/10): muda o texto do contrato, sem nome, local ou atividade novos.
  'clausulas',
]);

export type RotaAlteracao = 'A' | 'B';

export interface RotaResultado {
  /** null = nenhum tipo marcado ainda (badge não aparece). */
  rota: RotaAlteracao | null;
  /** Explicação curta pra UI (badge/tooltip). */
  motivo: string;
  /** true quando a classificação é incerta (ex: `outras`) — pinta como atenção. */
  revisar: boolean;
}

export function classificarRotaAlteracao(tipos: TipoAlteracao[] | undefined | null): RotaResultado {
  const lista = tipos ?? [];
  const temA = lista.some(t => ROTA_A_TIPOS.has(t));
  const temB = lista.some(t => ROTA_B_TIPOS.has(t));
  const temOutras = lista.includes('outras');

  // `outras` sozinho (ou sem A/B claro): indeterminado → provável A, revisar.
  if (temOutras && !temA && !temB) {
    return {
      rota: 'A',
      motivo: '"Outras" é indeterminado — trate como Rota A (com viabilidade) e revise.',
      revisar: true,
    };
  }
  if (temA) {
    return {
      rota: 'A',
      motivo: temB
        ? 'Muda também nome/endereço/objeto/natureza jurídica — como exige viabilidade, é uma viabilidade só (Rota A).'
        : 'Muda nome, endereço, objeto ou natureza jurídica → exige consulta de viabilidade prévia.',
      revisar: false,
    };
  }
  if (temB) {
    return {
      rota: 'B',
      motivo:
        'Muda só capital, quadro de sócios ou administração, ou encerra filial → sem viabilidade (transmite no REDESIM).',
      revisar: false,
    };
  }
  return {
    rota: null,
    motivo: 'Marque o que está sendo alterado para ver a rota.',
    revisar: false,
  };
}
